import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import theme from "../theme";
import greenUnderline from "../images/green_underline.png";
import { useUser } from "../contexts/UserContext";

const TestComplete: React.FC = () => {
  const navigate = useNavigate();
  const { isExternalApiUser, logout } = useUser();
  const isExternal = isExternalApiUser();
  // Once the candidate clicks "Close tab" we lock the view into a
  // session-ended state. This is needed because logout() clears the user
  // from context, which would flip isExternalApiUser() to false on re-render
  // and briefly show the normal-user variant before window.close() runs (and
  // typically silently fails for tabs not opened via window.open).
  const [sessionEnded, setSessionEnded] = useState(false);

  const handleCloseTab = async () => {
    // Lock the variant FIRST so the post-logout re-render doesn't flash the
    // wrong copy / button while window.close() does or doesn't take effect.
    setSessionEnded(true);
    // 1. Exit fullscreen — the test entered fullscreen on start (see
    //    BeginTest.handleStartTest), and the page may still be in that state.
    if (document.fullscreenElement) {
      try { await document.exitFullscreen(); } catch { /* ignore */ }
    }
    // 2. Revoke the DRF auth token server-side and wipe the local copy so
    //    the magic-link session can't be reused if the candidate doesn't
    //    actually close the browser. logout() handles both API call +
    //    localStorage cleanup; swallow errors so the close still proceeds.
    try { await logout(); } catch { /* ignore */ }
    // 3. Close the tab. Only works for tabs opened via window.open(); for
    //    direct magic-link clicks, the candidate will have to close manually
    //    — but at this point the credential is already revoked and the
    //    session-ended message tells them what to do.
    window.close();
  };

  if (sessionEnded) {
    return (
      <div style={container}>
        <div style={wrapper}>
          <div style={card}>
            <h1 style={title}>
              Session ended
              <img
                src={greenUnderline}
                alt=""
                style={{ height: 10, display: "block", margin: "0.25rem 55% 0" }}
              />
            </h1>
            <p style={paragraph}>
              You can now close this tab. If your browser didn't close it
              automatically, please close it manually.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={container}>
      <div style={wrapper}>
        <div style={card}>
          {/* Heading */}
          <h1 style={title}>
            Assessment complete!
            <img
              src={greenUnderline}
              alt=""
              style={{ height: 10, display: "block", margin: "0.25rem 55% 0" }}
            />
          </h1>

          <p style={paragraph}>Thanks for completing the assessment!</p>
          {isExternal ? (
            <p style={paragraph}>
              Your results have been sent to the recruiter or employer who
              invited you. You can now close this window.
            </p>
          ) : (
            <p style={paragraph}>
              You can check your results in your dashboard. You can also download
              your certificate from there.
            </p>
          )}

          {!isExternal && (
            <div style={scoreNote}>
              <strong>A note on your score:</strong> your result is weighted by question
              difficulty, so harder questions are worth more points than easier ones.
              This means your overall score may differ from the percentage of
              questions you answered correctly.
            </div>
          )}

          <div style={btnRow}>
            {isExternal ? (
              <button style={primaryBtn} onClick={handleCloseTab}>
                Close tab
              </button>
            ) : (
              <button style={primaryBtn} onClick={() => navigate("/test-dashboard")}>
                To my dashboard
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const container: React.CSSProperties = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "calc(100vh - 58px)"
};

const wrapper: React.CSSProperties = {
  width: "100%",
  maxWidth: 540,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "1.75rem", // spacing between progress bar and card
};

const card: React.CSSProperties = {
  width: "100%",
  maxWidth: 540,
  padding: "3rem 2rem 3.5rem",
  background: "#ffffff",
  textAlign: "center",
  borderRadius: 8,
  boxShadow: "0px 0px 32px 0px #1D1F480A"
};

const title: React.CSSProperties = {
  margin: 0,
  color: theme.colors.primary,
  fontSize: 36,
  fontWeight: 700,
  lineHeight: 1.15,
};

const paragraph: React.CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: "#000",
  marginTop: "1.5rem",
};

const scoreNote: React.CSSProperties = {
  fontSize: 13,
  lineHeight: 1.55,
  color: "#374151",
  textAlign: "left",
  marginTop: "1.75rem",
  padding: "14px 16px",
  background: "#F0F9FF",
  border: "1px solid #BAE6FD",
  borderLeft: "3px solid #0EA5E9",
  borderRadius: 6,
};

const btnRow: React.CSSProperties = {
  display: "flex",
  gap: "1rem",
  justifyContent: "center",
  flexWrap: "wrap",
  marginTop: "2.5rem",
};

const baseBtn: React.CSSProperties = {
  flex: "1 1 180px",
  padding: "0.85rem",
  borderRadius: 9999,
  fontSize: "1rem",
  fontWeight: 600,
  cursor: "pointer",
};

const primaryBtn: React.CSSProperties = {
  ...baseBtn,
  background: theme.colors.primary,
  color: "#fff",
  border: "none",
  maxWidth: "250px",
};

const outlineBtn: React.CSSProperties = {
  ...baseBtn,
  background: "transparent",
  color: theme.colors.primary,
  border: `2px solid ${theme.colors.primary}`,
};

export default TestComplete;
